import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TemperatureBadge, StatusBadge } from "@/components/crm/Badges";
import UnassignedLeads from "@/components/crm/UnassignedLeads";
import SyncAlertBanner from "@/components/crm/SyncAlertBanner";
import AssignmentMonitor, { NumBtn } from "@/components/crm/AssignmentMonitor";
import { usePageState } from "@/hooks/usePageState";
import { fullName, relDate, isOverdue, isInvalidPhone } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Flame, Phone, AlertTriangle, UserPlus, CalendarClock, Ban,
  ArrowUpRight, Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";

const CARDS = [
  { key: "priorita_alta", label: "Priorità Alta", icon: Flame, color: "text-red-500", ring: "ring-red-500/20", bg: "bg-red-500/10" },
  { key: "followup_oggi", label: "Follow-up Oggi", icon: Phone, color: "text-blue-500", ring: "ring-blue-500/20", bg: "bg-blue-500/10" },
  { key: "followup_ritardo", label: "Follow-up in Ritardo", icon: AlertTriangle, color: "text-amber-500", ring: "ring-amber-500/20", bg: "bg-amber-500/10" },
  { key: "nuovi_lead", label: "Nuovi Lead", icon: UserPlus, color: "text-sky-500", ring: "ring-sky-500/20", bg: "bg-sky-500/10" },
  { key: "appuntamenti_oggi", label: "Appuntamenti Oggi", icon: CalendarClock, color: "text-emerald-500", ring: "ring-emerald-500/20", bg: "bg-emerald-500/10" },
  { key: "no_show", label: "No-show da Recuperare", icon: Ban, color: "text-zinc-500", ring: "ring-zinc-500/20", bg: "bg-zinc-500/10" },
];

const REASON_STYLE = {
  overdue: "text-amber-600 dark:text-amber-400",
  appointment: "text-emerald-600 dark:text-emerald-400",
  new: "text-sky-600 dark:text-sky-400",
  no_show: "text-zinc-600 dark:text-zinc-400",
  followup: "text-blue-600 dark:text-blue-400",
};

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/commerciale").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const c = data.counts;
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";

  return (
    <div className="space-y-6 animate-fade-up">
      {/* Greeting */}
      <div>
        <div className="overline text-muted-foreground">{greet}</div>
        <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight">{data.greeting_name}</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {isAdmin ? "Panoramica generale del team. Assegna i nuovi lead in arrivo e monitora l'operatività." : (<>
          Oggi hai <b className="text-foreground">{c.priorita_alta}</b> lead ad alta priorità,{" "}
          <b className="text-foreground">{c.followup_oggi + c.followup_ritardo}</b> follow-up,{" "}
          <b className="text-foreground">{c.appuntamenti_oggi}</b> appuntamenti e{" "}
          <b className="text-foreground">{c.nuovi_lead}</b> nuovi lead.</>)}
        </p>
      </div>

      {/* Admin: avviso sincronizzazioni */}
      {isAdmin && <SyncAlertBanner />}

      {/* Admin: lead da assegnare */}
      {isAdmin && <UnassignedLeads />}

      {/* Admin: lead da gestire per commerciale */}
      {isAdmin && <WorkloadTable navigate={navigate} />}

      {/* Monitoraggio lead assegnati (admin) */}
      {isAdmin && <AssignmentMonitor navigate={navigate} isAdmin />}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {CARDS.map((card) => (
          <div key={card.key} data-testid={`dash-card-${card.key}`}
            className={cn("rounded-lg border bg-card p-4 flex flex-col gap-3 transition-transform hover:-translate-y-0.5")}>
            <div className={cn("w-9 h-9 rounded-md grid place-items-center", card.bg, card.color)}>
              <card.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading text-3xl font-black tabular-nums">{c[card.key]}</div>
              <div className="text-xs text-muted-foreground mt-0.5 leading-tight">{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Da fare oggi */}
      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="font-heading font-bold text-lg">Da fare oggi</h2>
          <span className="text-xs text-muted-foreground">{data.todo.length} attività · ordinate per priorità</span>
        </div>
        {data.todo.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm">Nessuna attività in programma. Ottimo lavoro! 🎉</div>
        ) : (
          <ul className="divide-y">
            {data.todo.map((item, i) => {
              const l = item.lead;
              const overdue = item.type === "overdue" || (item.when && item.type !== "new" && isOverdue(item.when));
              return (
                <li key={i} data-testid={`todo-item-${l.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors group">
                  <TemperatureBadge value={l.temperature} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold truncate">{fullName(l)}</span>
                      <span className="text-xs text-muted-foreground truncate hidden sm:inline">· {l.company}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs">
                      <span className={cn("font-medium", overdue ? REASON_STYLE.overdue : REASON_STYLE[item.type] || "text-muted-foreground")}>
                        {overdue && "⚠️ "}{item.reason}
                      </span>
                      {item.when && <span className="text-muted-foreground">· {relDate(item.when)}</span>}
                      {(l.interests || []).slice(0, 1).map((it) => (
                        <span key={it} className="text-muted-foreground hidden md:inline">· {it}</span>
                      ))}
                    </div>
                  </div>
                  <StatusBadge value={l.status} className="hidden lg:inline-flex" />
                  <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    {l.phone && !isInvalidPhone(l.phone) && (
                      <a href={`tel:${l.phone}`} data-testid={`todo-call-${l.id}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-emerald-500 text-white hover:bg-emerald-600 transition-colors">
                        <Phone className="w-4 h-4" />
                      </a>
                    )}
                    <Button size="sm" variant="secondary" data-testid={`todo-open-${l.id}`}
                      onClick={() => navigate(`/leads/${l.id}`)} className="gap-1">
                      Apri <ArrowUpRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Commerciale: monitoraggio dei propri lead assegnati (in fondo, layout esistente invariato) */}
      {!isAdmin && <AssignmentMonitor navigate={navigate} isAdmin={false} />}
    </div>
  );
}

function WorkloadTable({ navigate }) {
  const [rows, setRows] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const [sel, setSel] = usePageState("dash_workload_sel", "all");
  useEffect(() => {
    api.get("/dashboard/workload").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/users").then((r) => setCommercials((r.data || []).filter((u) => u.role === "commerciale"))).catch(() => {});
  }, []);
  if (!rows) return null;
  const go = (uid, bucket) => navigate(`/leads?assigned_to=${uid}&bucket=${bucket}`);
  const COLS = [
    { key: "never_contacted", label: "Mai contattati" },
    { key: "to_recall", label: "Da richiamare" },
    { key: "no_next_action", label: "Senza prossima azione" },
  ];

  const nameMap = {};
  rows.forEach((r) => { nameMap[r.user_id] = r.name; });
  commercials.forEach((u) => { if (!nameMap[u.id]) nameMap[u.id] = u.name; });
  const options = Object.entries(nameMap).map(([id, name]) => ({ id, name }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const zeroRow = (id) => ({ user_id: id, name: nameMap[id] || "—", color: null,
    assigned_total: 0, never_contacted: 0, to_recall: 0, no_next_action: 0, da_gestire: 0 });
  const display = sel === "all" ? rows : [rows.find((r) => r.user_id === sel) || zeroRow(sel)];

  return (
    <div className="rounded-lg border bg-card overflow-hidden" data-testid="workload-section">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b flex-wrap">
        <h2 className="font-heading font-bold text-lg">Lead da gestire per commerciale</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground hidden md:inline">Clicca un numero per aprire l'elenco</span>
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="h-8 w-56" data-testid="workload-commercial-filter">
              <SelectValue placeholder="Tutti i commerciali" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="workload-opt-all">Tutti i commerciali</SelectItem>
              {options.map((o) => (
                <SelectItem key={o.id} value={o.id} data-testid={`workload-opt-${o.id}`}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {display.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground text-sm">Nessun lead assegnato ai commerciali.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b bg-muted/30">
                <th className="px-4 py-2.5 font-medium">Commerciale</th>
                <th className="px-3 py-2.5 font-medium text-right">Assegnati</th>
                <th className="px-3 py-2.5 font-medium text-right">Mai contattati</th>
                <th className="px-3 py-2.5 font-medium text-right">Da richiamare</th>
                <th className="px-3 py-2.5 font-medium text-right">Senza prossima azione</th>
                <th className="px-3 py-2.5 font-bold text-right text-foreground">Da gestire</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {display.map((r) => (
                <tr key={r.user_id} data-testid={`workload-row-${r.user_id}`} className="hover:bg-muted/40 transition-colors">
                  <td className="px-4 py-2.5 font-semibold whitespace-nowrap">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: r.color || "#9ca3af" }} />
                      {r.name}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">{r.assigned_total}</td>
                  {COLS.map((c) => (
                    <td key={c.key} className="px-3 py-2.5 text-right">
                      <NumBtn n={r[c.key]} onClick={() => go(r.user_id, c.key)} testid={`workload-${c.key}-${r.user_id}`} />
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-right">
                    <NumBtn n={r.da_gestire} strong onClick={() => go(r.user_id, "da_gestire")} testid={`workload-da_gestire-${r.user_id}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
